/*
 * FREE SHIPPING PROGRESS BAR
 * Self-contained utility — no Liquid changes needed.
 * - Renders in both the cart drawer and main cart footer.
 * - Text follows the page's active language (via <html lang="...">).
 * - The dollar amount is highlighted in red.
 */

if (typeof window !== 'undefined' && !window.simsaFreeShipping) {
  window.simsaFreeShipping = {
    // 500,000 COP threshold.
    // Shopify cart.js amounts are in the smallest unit x100.
    thresholdCents: 500000 * 100,

    translations: {
      en: {
        add: (amount) =>
          `Add <span class="free-shipping-bar__amount">$${amount} COP</span> more for `,
        addBold: 'Free Shipping',
        complete: '🎉 You\'ve unlocked Free Shipping!',
      },

      es: {
        add: (amount) =>
          `Añade <span class="free-shipping-bar__amount">$${amount} COP</span> más para `,
        addBold: 'Envío Gratis',
        complete: '🎉 ¡Tienes Envío Gratis!',
      },
    },

    getLang() {
      const lang = (document.documentElement.lang || 'en')
        .slice(0, 2)
        .toLowerCase();

      return this.translations[lang] ? lang : 'en';
    },

    getLocale() {
      return this.getLang() === 'es' ? 'es-CO' : 'en-US';
    },

    ensureStyles() {
      if (document.getElementById('FreeShippingBarStyles')) return;

      const style = document.createElement('style');

      style.id = 'FreeShippingBarStyles';

      style.textContent = `
        .free-shipping-bar {
          padding: 0 0 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid rgba(0,0,0,0.08);
        }

        .free-shipping-bar__text {
          margin: 0 0 8px;
          font-size: 1.3rem;
        }

        .free-shipping-bar__amount {
          color: #d0121a;
          font-weight: 600;
        }

        .free-shipping-bar__track {
          height: 6px;
          border-radius: 999px;
          background: rgba(0,0,0,0.08);
          overflow: hidden;
        }

        .free-shipping-bar__fill {
          height: 100%;
          background: #000;
          transition: width 0.3s ease;
          border-radius: 999px;
        }

        .free-shipping-bar--complete .free-shipping-bar__text {
          color: #1a7f37;
        }
      `;

      document.head.appendChild(style);
    },

    formatCOP(cents) {
      const value = Math.round(cents / 100);

      return value.toLocaleString(this.getLocale());
    },

    /*
     * Get BOTH possible cart footer containers.
     *
     * Cart Drawer:
     * .cart-drawer__footer
     *
     * Main Cart Page:
     * #main-cart-footer
     */
    getContainers() {
      const containers = [];

      const drawerFooter = document.querySelector('.cart-drawer__footer');

      const mainCartFooter = document.getElementById('main-cart-footer');

      if (drawerFooter) {
        containers.push(drawerFooter);
      }

      if (mainCartFooter) {
        containers.push(mainCartFooter);
      }

      return containers;
    },

    /*
     * Kept for compatibility with any existing code
     * that calls getContainer().
     */
    getContainer() {
      return this.getContainers()[0] || null;
    },

    /*
     * Update one specific container.
     */
    async updateContainer(target, cart) {
      if (!target) return;

      this.ensureStyles();

      let bar = target.querySelector('.free-shipping-bar');

      if (!bar) {
        bar = document.createElement('div');

        bar.className = 'free-shipping-bar';

        target.insertBefore(bar, target.firstChild);
      }

      const t = this.translations[this.getLang()];

      if (!cart || !cart.item_count) {
        bar.innerHTML = '';
        return;
      }

      const remaining = this.thresholdCents - cart.total_price;

      if (remaining > 0) {
        bar.classList.remove('free-shipping-bar--complete');

        const pct = Math.min(
          100,
          (cart.total_price / this.thresholdCents) * 100
        );

        bar.innerHTML = `
          <p class="free-shipping-bar__text">
            ${t.add(this.formatCOP(remaining))}
            <strong>${t.addBold}</strong>
          </p>

          <div class="free-shipping-bar__track">
            <div
              class="free-shipping-bar__fill"
              style="width:${pct}%"
            ></div>
          </div>
        `;
      } else {
        bar.classList.add('free-shipping-bar--complete');

        bar.innerHTML = `
          <p class="free-shipping-bar__text">
            ${t.complete}
          </p>
        `;
      }
    },

    /*
     * Update the free-shipping bar.
     *
     * IMPORTANT:
     * When no container is supplied, update BOTH:
     * - Cart Drawer
     * - Main Cart Page
     *
     * This fixes the original issue where only the drawer
     * was receiving the free-shipping bar.
     */
    async update(container) {
      try {
        const response = await fetch('/cart.js');

        const cart = await response.json();

        if (container) {
          await this.updateContainer(container, cart);
          return;
        }

        const containers = this.getContainers();

        if (!containers.length) return;

        await Promise.all(
          containers.map((target) =>
            this.updateContainer(target, cart)
          )
        );
      } catch (e) {
        console.error(
          'Free shipping bar update failed:',
          e
        );
      }
    },
  };
}


class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems =
        this.closest('cart-items') ||
        this.closest('cart-drawer-items');

      cartItems.updateQuantity(
        this.dataset.index,
        0,
        event
      );
    });
  }
}

customElements.define(
  'cart-remove-button',
  CartRemoveButton
);


class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById(
        'shopping-cart-line-item-status'
      ) ||
      document.getElementById(
        'CartDrawer-LineItemStatus'
      );

    const debouncedOnChange = debounce(
      (event) => {
        this.onChange(event);
      },
      ON_CHANGE_DEBOUNCE_TIMER
    );

    this.addEventListener(
      'change',
      debouncedOnChange.bind(this)
    );
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === 'cart-items') {
          return;
        }

        return this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) ||
      this.querySelector(`#Drawer-quantity-${id}`);

    if (!input) return;

    input.value = input.getAttribute('value');

    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);

    event.target.reportValidity();

    this.resetQuantityInput(index);

    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(
      event.target.value
    );

    const index =
      event.target.dataset.index;

    let message = '';

    if (
      inputValue <
      event.target.dataset.min
    ) {
      message =
        window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min
        );
    } else if (
      inputValue >
      parseInt(event.target.max)
    ) {
      message =
        window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max
        );
    } else if (
      inputValue %
        parseInt(event.target.step) !==
      0
    ) {
      message =
        window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step
        );
    }

    if (message) {
      this.setValidity(
        event,
        index,
        message
      );
    } else {
      event.target.setCustomValidity('');

      event.target.reportValidity();

      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute(
          'name'
        ),
        event.target.dataset
          .quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (
      this.tagName ===
      'CART-DRAWER-ITEMS'
    ) {
      return fetch(
        `${routes.cart_url}?section_id=cart-drawer`
      )
        .then((response) =>
          response.text()
        )
        .then((responseText) => {
          const html =
            new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

          const selectors = [
            'cart-drawer-items',
            '.cart-drawer__footer',
          ];

          for (const selector of selectors) {
            const targetElement =
              document.querySelector(
                selector
              );

            const sourceElement =
              html.querySelector(
                selector
              );

            if (
              targetElement &&
              sourceElement
            ) {
              targetElement.replaceWith(
                sourceElement
              );
            }
          }

          /*
           * Refresh BOTH free-shipping bars.
           */
          if (
            window.simsaFreeShipping
          ) {
            window.simsaFreeShipping.update();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(
        `${routes.cart_url}?section_id=main-cart-items`
      )
        .then((response) =>
          response.text()
        )
        .then((responseText) => {
          const html =
            new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

          const sourceQty =
            html.querySelector(
              'cart-items'
            );

          this.innerHTML =
            sourceQty.innerHTML;

          /*
           * Refresh BOTH free-shipping bars.
           */
          if (
            window.simsaFreeShipping
          ) {
            window.simsaFreeShipping.update();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section:
          document.getElementById(
            'main-cart-items'
          ).dataset.id,
        selector: '.js-contents',
      },

      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },

      {
        id: 'cart-live-region-text',
        section:
          'cart-live-region-text',
        selector: '.shopify-section',
      },

      {
        id: 'main-cart-footer',
        section:
          document.getElementById(
            'main-cart-footer'
          ).dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(
    line,
    quantity,
    event,
    name,
    variantId
  ) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections:
        this.getSectionsToRender().map(
          (section) =>
            section.section
        ),
      sections_url:
        window.location.pathname,
    });

    const eventTarget =
      event.currentTarget instanceof
      CartRemoveButton
        ? 'clear'
        : 'change';

    fetch(
      `${routes.cart_change_url}`,
      {
        ...fetchConfig(),
        ...{
          body,
        },
      }
    )
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState =
          JSON.parse(state);

        CartPerformance.measure(
          `${eventTarget}:paint-updated-sections"`,
          () => {
            const quantityElement =
              document.getElementById(
                `Quantity-${line}`
              ) ||
              document.getElementById(
                `Drawer-quantity-${line}`
              );

            const items =
              document.querySelectorAll(
                '.cart-item'
              );

            if (parsedState.errors) {
              quantityElement.value =
                quantityElement.getAttribute(
                  'value'
                );

              this.updateLiveRegions(
                line,
                parsedState.errors
              );

              return;
            }

            this.classList.toggle(
              'is-empty',
              parsedState.item_count === 0
            );

            const cartDrawerWrapper =
              document.querySelector(
                'cart-drawer'
              );

            const cartFooter =
              document.getElementById(
                'main-cart-footer'
              );

            if (cartFooter) {
              cartFooter.classList.toggle(
                'is-empty',
                parsedState.item_count === 0
              );
            }

            if (cartDrawerWrapper) {
              cartDrawerWrapper.classList.toggle(
                'is-empty',
                parsedState.item_count === 0
              );
            }

            this.getSectionsToRender().forEach(
              (section) => {
                const sectionElement =
                  document.getElementById(
                    section.id
                  );

                if (!sectionElement) return;

                const elementToReplace =
                  sectionElement.querySelector(
                    section.selector
                  ) ||
                  sectionElement;

                const sectionHTML =
                  parsedState.sections[
                    section.section
                  ];

                if (!sectionHTML) return;

                elementToReplace.innerHTML =
                  this.getSectionInnerHTML(
                    sectionHTML,
                    section.selector
                  );
              }
            );

            const updatedValue =
              parsedState.items[line - 1]
                ? parsedState.items[line - 1]
                    .quantity
                : undefined;

            let message = '';

            if (
              items.length ===
                parsedState.items.length &&
              updatedValue !==
                parseInt(
                  quantityElement.value
                )
            ) {
              if (
                typeof updatedValue ===
                'undefined'
              ) {
                message =
                  window.cartStrings.error;
              } else {
                message =
                  window.cartStrings.quantityError.replace(
                    '[quantity]',
                    updatedValue
                  );
              }
            }

            this.updateLiveRegions(
              line,
              message
            );

            const lineItem =
              document.getElementById(
                `CartItem-${line}`
              ) ||
              document.getElementById(
                `CartDrawer-Item-${line}`
              );

            if (
              lineItem &&
              lineItem.querySelector(
                `[name="${name}"]`
              )
            ) {
              cartDrawerWrapper
                ? trapFocus(
                    cartDrawerWrapper,
                    lineItem.querySelector(
                      `[name="${name}"]`
                    )
                  )
                : lineItem
                    .querySelector(
                      `[name="${name}"]`
                    )
                    .focus();
            } else if (
              parsedState.item_count ===
                0 &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper.querySelector(
                  '.drawer__inner-empty'
                ),
                cartDrawerWrapper.querySelector(
                  'a'
                )
              );
            } else if (
              document.querySelector(
                '.cart-item'
              ) &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper,
                document.querySelector(
                  '.cart-item__name'
                )
              );
            }

            /*
             * Refresh BOTH free-shipping bars
             * after quantity/delete updates.
             */
            if (
              window.simsaFreeShipping
            ) {
              window.simsaFreeShipping.update();
            }
          }
        );

        CartPerformance.measureFromEvent(
          `${eventTarget}:user-action`,
          event
        );

        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source: 'cart-items',
            cartData: parsedState,
            variantId: variantId,
          }
        );
      })
      .catch(() => {
        this.querySelectorAll(
          '.loading__spinner'
        ).forEach((overlay) =>
          overlay.classList.add(
            'hidden'
          )
        );

        const errors =
          document.getElementById(
            'cart-errors'
          ) ||
          document.getElementById(
            'CartDrawer-CartErrors'
          );

        errors.textContent =
          window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(
    line,
    message
  ) {
    const lineItemError =
      document.getElementById(
        `Line-item-error-${line}`
      ) ||
      document.getElementById(
        `CartDrawer-LineItemError-${line}`
      );

    if (lineItemError) {
      lineItemError
        .querySelector(
          '.cart-item__error-text'
        )
        .textContent = message;
    }

    this.lineItemStatusElement.setAttribute(
      'aria-hidden',
      true
    );

    const cartStatus =
      document.getElementById(
        'cart-live-region-text'
      ) ||
      document.getElementById(
        'CartDrawer-LiveRegionText'
      );

    cartStatus.setAttribute(
      'aria-hidden',
      false
    );

    setTimeout(() => {
      cartStatus.setAttribute(
        'aria-hidden',
        true
      );
    }, 1000);
  }

  getSectionInnerHTML(
    html,
    selector
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector)
      .innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    mainCartItems.classList.add(
      'cart__items--disabled'
    );

    const cartItemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading__spinner`
      );

    const cartDrawerItemElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading__spinner`
      );

    [
      ...cartItemElements,
      ...cartDrawerItemElements,
    ].forEach((overlay) =>
      overlay.classList.remove(
        'hidden'
      )
    );

    document.activeElement.blur();

    this.lineItemStatusElement.setAttribute(
      'aria-hidden',
      false
    );
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    mainCartItems.classList.remove(
      'cart__items--disabled'
    );

    const cartItemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading__spinner`
      );

    const cartDrawerItemElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading__spinner`
      );

    cartItemElements.forEach(
      (overlay) =>
        overlay.classList.add(
          'hidden'
        )
    );

    cartDrawerItemElements.forEach(
      (overlay) =>
        overlay.classList.add(
          'hidden'
        )
    );
  }
}

customElements.define(
  'cart-items',
  CartItems
);


if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce(
            (event) => {
              const body =
                JSON.stringify({
                  note: event.target.value,
                });

              fetch(
                `${routes.cart_update_url}`,
                {
                  ...fetchConfig(),
                  ...{
                    body,
                  },
                }
              ).then(() =>
                CartPerformance.measureFromEvent(
                  'note-update:user-action',
                  event
                )
              );
            },
            ON_CHANGE_DEBOUNCE_TIMER
          )
        );
      }
    }
  );
}


document.addEventListener(
  'DOMContentLoaded',
  () => {
    if (
      window.simsaFreeShipping
    ) {
      window.simsaFreeShipping.update();
    }
  }
);